import React, { useState, useEffect } from "react";
import { Table, Button, Icon, Input, Image, Dropdown, Checkbox } from "semantic-ui-react";
import { every } from "lodash";
import { usePayment, useOrder, useSiteConfig, useInvoicing, useCash } from "../../../../hooks";
import {
  ORDER_STATUS,
  PAYMENT_METHODS,
  getPaymentMethod,
  getDeliveryChannel,
} from "../../../../utils/constants";
import { ModalConfirm } from "../../../Common";
import { getTableLabel, getTablePlace } from "../../../../utils/table";
import { printTicket } from "../../../../utils/ticket";
import { getOrderPrice } from "../../../../utils/order";
import { toast } from "react-toastify";
import { DiscountForm } from "../DiscountForm";
import { TipForm } from "./TipForm";
import { MixedPayment, partsProblem, settleParts } from "./MixedPayment";
import { showIssuedDocument } from "../../../../utils/issuedDocument";
import "./PaymentDetail.scss";

export function PaymentDetail(props) {
  // readOnly: vista del mozo desde la mesa — ve la cuenta, no la cobra
  // (se cobra en Caja → Cobrar; users/permissions.py lo exige en el backend).
  const { orders, openCloseModal, onReloadOrders, onTableClosed, readOnly = false } = props;
  // Copia local: al aplicar un descuento el backend devuelve el pago con el
  // total nuevo, y se muestra al instante sin esperar el refresco de la mesa.
  const [payment, setPayment] = useState(props.payment);
  useEffect(() => setPayment(props.payment), [props.payment]);
  const [showDiscount, setShowDiscount] = useState(false);
  const { closePayment, discountPayment } = usePayment();
  const { closeOrder } = useOrder();
  // Comprobante: "Nota de venta" (como siempre) o boleta/factura electrónica
  // cuando la facturación está activa (hoy solo modo prueba).
  const { issuer, getIssuer, emitComprobante, downloadComprobantePdf } = useInvoicing();
  const [docType, setDocType] = useState(props.payment.requested_doc_type || "NOTA");
  // Opciones del cobro (como Restaurant.pe): "Venta por consumo" = el
  // comprobante lleva una sola línea y no lista los productos; "Descargar PDF"
  // lo baja solo al cobrar. Por consumo arranca siempre apagado (que ninguna
  // venta salga sin detalle por descuido); el PDF recuerda la última elección
  // de este dispositivo.
  const [summarized, setSummarized] = useState(false);
  const [autoPdf, setAutoPdf] = useState(() => {
    try {
      return localStorage.getItem("icard.autoPdf") === "1";
    } catch (error) {
      return false;
    }
  });
  const changeAutoPdf = (value) => {
    setAutoPdf(value);
    try {
      localStorage.setItem("icard.autoPdf", value ? "1" : "0");
    } catch (error) {
      // sin almacenamiento: solo dura mientras la pantalla esté abierta
    }
  };
  const [showConfirm, setShowConfirm] = useState(false);
  const [operationCode, setOperationCode] = useState("");
  // Efectivo: cuánto entrega el cliente → vuelto (sin botones de billetes,
  // decisión del usuario: solo el monto y el vuelto calculado).
  const [cashReceived, setCashReceived] = useState("");
  // Pago mixto (ej. parte en efectivo y el resto con Yape): se arma aquí, al
  // cobrar — la forma elegida al pedir la cuenta queda como primera fila. Si
  // la cuenta se pidió como "Mixto" (el cliente avisó), abre ya dividido.
  const announcedMixed = props.payment.paymentType === "MIXED";
  const [mixed, setMixed] = useState(announcedMixed);
  const [parts, setParts] = useState(
    announcedMixed
      ? [
          { method: "CASH", amount: "", operation_code: "" },
          { method: "YAPE", amount: "", operation_code: "" },
        ]
      : []
  );
  // Forma de pago al cobrar: arranca con la que se eligió al pedir la cuenta,
  // pero el cliente puede cambiar de idea (pidió en efectivo y al final paga
  // todo con Yape, o al revés) — el cajero la cambia aquí. Si avisó "Mixto"
  // y al final paga en una sola forma, la elige desde cero.
  const [singleMethod, setSingleMethod] = useState(
    announcedMixed ? null : props.payment.paymentType
  );
  const singleType = singleMethod;
  const total = Number(payment.totalPayment);
  // Pago mixto ya "asentado": si en efectivo se escribió de más, el exceso es
  // vuelto (ver settleParts) y lo escrito cuenta como efectivo entregado.
  const settled = mixed ? settleParts(parts, total) : null;
  const cashDue = mixed
    ? settled.parts.filter((p) => p.method === "CASH").reduce((sum, p) => sum + Number(p.amount), 0)
    : total;
  const isCash = mixed ? settled.cashGiven > 0 : singleType === "CASH";
  const received =
    cashReceived !== ""
      ? Number(cashReceived)
      : mixed && settled.change > 0
      ? settled.cashGiven
      : null;
  const change = received != null && !Number.isNaN(received) ? received - cashDue : null;
  const cashShort = change != null && change < 0;
  const fee = Number(payment.delivery_fee || 0);
  const channelText = getDeliveryChannel(payment.delivery_channel)?.text;
  const { siteConfig, getSiteConfig } = useSiteConfig();
  const method = getPaymentMethod(mixed ? "MIXED" : singleType);
  // Yape/Plin: flujo de cobro con QR (mismo modelo que el cobro con Yape de
  // SIG) — el cajero muestra el QR del negocio, el cliente paga desde su app
  // y el cajero copia el código de operación antes de confirmar.
  const isWallet = !mixed && ["YAPE", "PLIN"].includes(singleType);
  const walletQr = singleType === "YAPE" ? siteConfig?.yape_qr : siteConfig?.plin_qr;

  // QR de Yape/Plin y nombre del negocio para el ticket.
  // Sin caja abierta no se cobra (lo exige también el backend): la Caja solo
  // cuenta lo cobrado después de abrirla. Si la consulta falla no se bloquea
  // desde aquí; el backend decide.
  const { session: cashSession, getCurrentCash } = useCash();
  const [cashChecked, setCashChecked] = useState(false);
  useEffect(() => {
    getSiteConfig();
    if (!readOnly) {
      getIssuer();
      getCurrentCash()
        .then(() => setCashChecked(true))
        .catch(() => {}); // sin respuesta no se avisa nada (mejor que un aviso falso)
    }
  }, []);
  const invoicingOn = !readOnly && !!issuer?.enabled;
  const customerDoc = payment.customer_data || {};
  const customerHasRuc = customerDoc.doc_type === "RUC" && /^\d{11}$/.test(customerDoc.doc_number || "");
  const customerHasDni = customerDoc.doc_type === "DNI" && /^\d{8}$/.test(customerDoc.doc_number || "");
  const allDelivered = every(
    orders,
    (order) => order.status === ORDER_STATUS.DELIVERED
  );

  const hasDiscount = Number(payment.discount_amount) > 0;
  const onApplyDiscount = async (discount) => {
    const updated = await discountPayment(payment.id, discount);
    setPayment(updated);
    setShowDiscount(false);
  };
  const onRemoveDiscount = async () => {
    setPayment(await discountPayment(payment.id, { value: 0 }));
  };

  // "Enviar por WhatsApp" (si el cliente tiene teléfono): abre WhatsApp con
  // el detalle listo para enviar — no es un comprobante SUNAT.
  const phoneDigits = (payment.customer_data?.phone || "").replace(/\D/g, "");
  const whatsappNumber =
    phoneDigits.length === 9 ? `51${phoneDigits}` : phoneDigits.length >= 11 ? phoneDigits : "";
  const whatsappUrl = whatsappNumber
    ? `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(
        [
          `*${siteConfig?.site_name || "Restaurante"}* — ${getTablePlace(orders[0] || payment)}`,
          ...orders.map(
            (order) =>
              `• ${order.product_data?.title || "Producto"}${
                order.is_courtesy ? " (cortesía)" : ""
              } — S/ ${getOrderPrice(order).toFixed(2)}`
          ),
          ...(hasDiscount ? [`Descuento: −S/ ${payment.discount_amount}`] : []),
          ...(fee > 0 ? [`Envío: S/ ${fee.toFixed(2)}`] : []),
          `*Total: S/ ${total.toFixed(2)}*`,
          "Gracias por tu pedido.",
        ].join("\n")
      )}`
    : null;

  const startMixed = () => {
    const first = singleType || "CASH";
    const second = first === "CASH" ? "YAPE" : "CASH";
    setParts([
      { method: first, amount: "", operation_code: operationCode.trim() },
      { method: second, amount: "", operation_code: "" },
    ]);
    setCashReceived("");
    setMixed(true);
  };
  const stopMixed = () => {
    setMixed(false);
    setParts([]);
    setCashReceived("");
  };
  const cleanParts = () =>
    settleParts(parts, total).parts.map((part) => ({
      method: part.method,
      amount: Number(part.amount).toFixed(2),
      operation_code: part.operation_code.trim(),
    }));

  // Por qué todavía no se puede cobrar — se muestra debajo del botón, para
  // que el cajero no quede adivinando por qué está apagado.
  const blockReason = cashChecked && !cashSession
    ? "Abrir Caja antes de proceder al cobro"
    : !allDelivered
    ? "Faltan platos por entregar"
    : mixed
    ? partsProblem(parts, total)
    : !singleType
    ? "Elige la forma de pago"
    : isWallet && !operationCode.trim()
    ? `Falta el código de operación de ${method?.text}`
    : cashShort
    ? `El efectivo recibido no alcanza`
    : invoicingOn && docType === "FACTURA" && !customerHasRuc
    ? payment.customer_data
      ? "Cliente registrado sin RUC"
      : "Para factura, elige un cliente con RUC"
    : invoicingOn && docType === "BOLETA" && total >= 700 && !customerHasDni && !customerHasRuc
    ? "Una boleta desde S/ 700 exige el DNI del cliente (elige un cliente con DNI)"
    : null;

  // Cierra la mesa en pantalla (después de cobrar, y de mostrar el comprobante
  // si se emitió uno).
  const finish = () => {
    onReloadOrders();
    openCloseModal();
    onTableClosed();
  };

  const onCloseTable = async () => {
    // Reparto con una sola forma con monto (ej. Efectivo 64 + Yape 0): se
    // cobra como pago simple de esa forma, no como mixto.
    const finalParts = mixed ? cleanParts() : null;
    const asSingle = finalParts && finalParts.length === 1 ? finalParts[0] : null;
    try {
      await closePayment(
        payment.id,
        asSingle ? asSingle.operation_code : mixed ? "" : operationCode.trim(),
        isCash && received != null && !cashShort ? received.toFixed(2) : null,
        asSingle ? null : finalParts,
        asSingle ? asSingle.method : !mixed && singleMethod !== payment.paymentType ? singleMethod : null
      );
    } catch (error) {
      toast.error(error.message);
      return;
    }

    for await (const order of orders) {
      await closeOrder(order.id);
    }

    // La venta ya está cobrada: si el comprobante falla (ej. falta una serie),
    // el cobro no se pierde — se avisa y se puede reintentar desde Historial.
    if (invoicingOn) {
      try {
        const comprobante = await emitComprobante(payment.id, docType, summarized);
        if (autoPdf) {
          try {
            await downloadComprobantePdf(comprobante);
          } catch (error) {
            toast.error(error.message);
          }
        }
        // Cuadro "Comprobante emitido" (Imprimir / PDF / Listo): se muestra desde
        // el layout, con copia de los datos — este panel se cierra solo en unos
        // segundos y los pedidos ya están cerrados. "Listo" ejecuta finish.
        showIssuedDocument({
          comprobante,
          orders: [...orders],
          payment: {
            ...payment,
            paymentType: mixed ? "MIXED" : singleType,
            ...(mixed ? { parts: cleanParts() } : {}),
          },
          issuer,
          siteName: siteConfig?.site_name,
          onDone: finish,
        });
        return;
      } catch (error) {
        toast.error(`Cobrado, pero no se pudo emitir el comprobante: ${error.message}`);
      }
    }
    finish();
  };

  return (
    <div className="payment-detail">
      <Table striped>
        <Table.Body>
          <Table.Row>
            <Table.Cell>Mesa:</Table.Cell>
            <Table.Cell>{getTableLabel(payment)}</Table.Cell>
          </Table.Row>
          {(hasDiscount || fee > 0) && (
            <Table.Row>
              <Table.Cell>Subtotal:</Table.Cell>
              <Table.Cell>S/ {payment.subtotal}</Table.Cell>
            </Table.Row>
          )}
          {hasDiscount && (
            <>
              <Table.Row className="payment-detail__discount-row">
                <Table.Cell>
                  Descuento
                  {payment.discount_type === "PERCENT" &&
                    ` (${Number(payment.discount_value)}%)`}
                  :
                </Table.Cell>
                <Table.Cell>
                  − S/ {payment.discount_amount}
                  <div className="payment-detail__discount-reason">
                    {payment.discount_reason}
                    {payment.discount_by_name && ` · ${payment.discount_by_name}`}
                  </div>
                </Table.Cell>
              </Table.Row>
            </>
          )}
          {fee > 0 && (
            <Table.Row>
              <Table.Cell>Envío{channelText && ` (${channelText})`}:</Table.Cell>
              <Table.Cell>S/ {fee.toFixed(2)}</Table.Cell>
            </Table.Row>
          )}
          <Table.Row>
            <Table.Cell>{hasDiscount || fee > 0 ? "Total a cobrar:" : "Total:"}</Table.Cell>
            <Table.Cell>
              <strong>S/ {payment.totalPayment}</strong>
            </Table.Cell>
          </Table.Row>
          {payment.customer_data && (
            <Table.Row>
              <Table.Cell>Cliente:</Table.Cell>
              <Table.Cell>
                {payment.customer_data.full_name}
                {payment.customer_data.doc_number &&
                  ` · ${payment.customer_data.doc_type} ${payment.customer_data.doc_number}`}
              </Table.Cell>
            </Table.Row>
          )}
          {payment.guests && (
            <Table.Row>
              <Table.Cell>Personas:</Table.Cell>
              <Table.Cell>
                {payment.guests} · S/{" "}
                {(Number(payment.totalPayment) / payment.guests).toFixed(2)} por persona
              </Table.Cell>
            </Table.Row>
          )}
          <Table.Row>
            <Table.Cell>Forma de pago:</Table.Cell>
            <Table.Cell>
              {readOnly ? (
                <>
                  <Icon name={getPaymentMethod(payment.paymentType)?.icon} />{" "}
                  {getPaymentMethod(payment.paymentType)?.text}
                </>
              ) : mixed ? (
                <>
                  <Icon name={method.icon} /> {method.text}
                </>
              ) : (
                <Dropdown
                  selection
                  compact
                  placeholder="Elige cómo paga"
                  options={PAYMENT_METHODS.map((m) => ({
                    key: m.value,
                    value: m.value,
                    text: m.text,
                    icon: m.icon,
                  }))}
                  value={singleMethod}
                  onChange={(_, data) => setSingleMethod(data.value)}
                />
              )}
            </Table.Cell>
          </Table.Row>
        </Table.Body>
      </Table>

      {siteConfig?.tips_enabled && payment.statusPayment === "PENDING" && (
        <TipForm payment={payment} onSaved={setPayment} />
      )}

      {readOnly ? (
        <p className="payment-detail__readonly">Esta cuenta se cobra en Caja.</p>
      ) : (
        <>

      {/* Descuento o cortesía, con motivo obligatorio (queda registrado). */}
      {showDiscount ? (
        <DiscountForm onApply={onApplyDiscount} onCancel={() => setShowDiscount(false)} />
      ) : hasDiscount ? (
        <Button basic fluid size="small" onClick={onRemoveDiscount} style={{ marginBottom: 10 }}>
          <Icon name="undo" /> Quitar descuento
        </Button>
      ) : (
        <Button
          basic
          fluid
          size="small"
          onClick={() => setShowDiscount(true)}
          style={{ marginBottom: 10 }}
          title="Descuento en S/ o % sobre la cuenta, o cortesía (100%). Pide un motivo y queda registrado a tu nombre en Reportes → Anulaciones y descuentos."
        >
          <Icon name="tag" /> Aplicar descuento o cortesía
        </Button>
      )}

      {/* Pago mixto: dividir la cuenta entre varias formas de pago. */}
      {mixed ? (
        <>
          <MixedPayment total={total} parts={parts} onChange={setParts} siteConfig={siteConfig} />
          <Button basic fluid size="small" onClick={stopMixed} style={{ marginBottom: 10 }}>
            <Icon name="undo" /> Cobrar con una sola forma de pago
          </Button>
        </>
      ) : (
        <Button
          basic
          fluid
          size="small"
          onClick={startMixed}
          style={{ marginBottom: 10 }}
          title="Cuando el cliente paga una parte en una forma y el resto en otra (ej. efectivo + Yape). La Caja y los reportes cuentan cada parte en su forma de pago."
        >
          <Icon name="random" /> Dividir el pago (mixto)
        </Button>
      )}

      {isWallet && (
        // key: al cambiar de Yape a Plin se vuelve a montar y el cursor cae
        // otra vez en el código de operación.
        <div className="payment-detail__wallet" key={method.value}>
          <h4>Pago con {method.text} / QR</h4>
          <ol>
            <li>Pide al cliente que escanee el código QR desde su app {method.text}.</li>
            <li>Una vez confirmado en su app, ingresa el código de operación.</li>
            <li>Verifica el monto (S/ {payment.totalPayment}) y confirma el pago.</li>
          </ol>

          {walletQr ? (
            <Image src={walletQr} className="payment-detail__qr" centered />
          ) : (
            <p className="payment-detail__no-qr">
              Aún no se subió el QR de {method.text}. Súbelo en Configuración →
              Pagos (Yape/Plin).
            </p>
          )}

          <div className="payment-detail__status">
            <Icon name="clock outline" /> Pendiente de confirmación
          </div>

          <label>Código de operación / Referencia</label>
          <Input
            fluid
            placeholder="Ej. 123456"
            autoFocus
            value={operationCode}
            onChange={(_, data) => setOperationCode(data.value)}
          />
        </div>
      )}

      {isCash && (
        <div className="payment-detail__cash">
          <div>
            <label>{mixed ? "Efectivo recibido" : "Monto recibido"}</label>
            <Input
              labelPosition="left"
              type="number"
              min="0"
              step="0.10"
              placeholder={(mixed && settled.change > 0 ? settled.cashGiven : cashDue).toFixed(2)}
              value={cashReceived}
              onChange={(_, data) => setCashReceived(data.value)}
              label={{ basic: true, content: "S/" }}
            />
          </div>
          <div>
            <label>Vuelto</label>
            <div
              className={`payment-detail__change${cashShort ? " payment-detail__change--short" : ""}`}
            >
              {change == null
                ? "—"
                : cashShort
                ? `Faltan S/ ${Math.abs(change).toFixed(2)}`
                : `S/ ${change.toFixed(2)}`}
            </div>
          </div>
        </div>
      )}

      {invoicingOn && (
        <div className="payment-detail__doc">
          <label>
            Comprobante <span className="payment-detail__doc-test">MODO PRUEBA</span>
          </label>
          <Button.Group fluid size="small">
            {[
              ["NOTA", "Nota de venta"],
              ["BOLETA", "Boleta"],
              ["FACTURA", "Factura"],
            ].map(([value, text]) => (
              <Button
                key={value}
                type="button"
                primary={docType === value}
                basic={docType !== value}
                onClick={() => setDocType(value)}
              >
                {text}
              </Button>
            ))}
          </Button.Group>
          <div className="payment-detail__doc-options">
            <Checkbox
              toggle
              label="Venta por consumo (sin detalle de productos)"
              checked={summarized}
              onChange={(_, data) => setSummarized(data.checked)}
            />
            <Checkbox
              toggle
              label="Descargar PDF al cobrar"
              checked={autoPdf}
              onChange={(_, data) => changeAutoPdf(data.checked)}
            />
          </div>
        </div>
      )}

      {whatsappUrl && (
        <Button
          basic
          color="green"
          fluid
          as="a"
          href={whatsappUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginBottom: 10 }}
          title="Abre WhatsApp con el detalle de la cuenta para enviarlo al cliente"
        >
          <Icon name="whatsapp" /> Enviar por WhatsApp a {payment.customer_data.full_name}
        </Button>
      )}

      <Button
        basic
        fluid
        style={{ marginBottom: 10 }}
        onClick={() =>
          printTicket({
            siteName: siteConfig?.site_name,
            orders,
            payment: {
              ...payment,
              ...(mixed
                ? { paymentType: "MIXED", parts: cleanParts() }
                : { paymentType: singleType }),
              operation_code: mixed ? "" : operationCode.trim(),
              cash_received: isCash && received != null && !cashShort ? received : null,
            },
            summaryLine:
              invoicingOn && summarized ? issuer?.summary_line_text || "Venta por consumo" : null,
            kind: "pago",
          })
        }
      >
        <Icon name="print" /> Imprimir ticket
      </Button>

      <Button
        primary
        fluid
        disabled={!!blockReason}
        onClick={() => setShowConfirm(true)}
      >
        Marcar como pagado y cerrar mesa
      </Button>
      {blockReason && <div className="payment-detail__blocked">{blockReason}</div>}


      <ModalConfirm
        title="¿Cerrar esta mesa?"
        content="Se marcará como pagada y quedará libre para nuevos clientes."
        show={showConfirm}
        onCloseText="Cancelar"
        onClose={() => setShowConfirm(false)}
        onConfirmText="Aceptar"
        onConfirm={async () => {
          setShowConfirm(false);
          await onCloseTable();
        }}
      />
        </>
      )}
    </div>
  );
}
